import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

interface Payout {
 _id: string;
 storeId: {
  _id: string;
  name: string;
 };
 amount: number;
 currency: string;
 status: "pending" | "processing" | "completed" | "failed" | "cancelled";
 payoutMethod: string;
 periodStart: string;
 periodEnd: string;
 createdAt: string;
 processedAt?: string;
}

interface PayoutStats {
 total: number;
 pending: number;
 processing: number;
 completed: number;
 failed: number;
 cancelled: number;
 totalAmount: number;
}

const PlatformPayouts: React.FC = () => {
 const { t } = useTranslation();
 const { isAuthenticated } = usePlatformAuth();
 const navigate = useNavigate();
 const [payouts, setPayouts] = useState<Payout[]>([]);
 const [stats, setStats] = useState<PayoutStats>({
  total: 0,
  pending: 0,
  processing: 0,
  completed: 0,
  failed: 0,
  cancelled: 0,
  totalAmount: 0,
 });
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const [filters, setFilters] = useState({
  storeId: "",
  status: "",
  startDate: "",
  endDate: "",
 });

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  fetchPayouts();
 }, [isAuthenticated, navigate, page, filters]);

 const fetchPayouts = async () => {
  try {
   setLoading(true);
   const params: any = { page, limit: 20 };
   if (filters.storeId) params.storeId = filters.storeId;
   if (filters.status) params.status = filters.status;
   if (filters.startDate) params.startDate = filters.startDate;
   if (filters.endDate) params.endDate = filters.endDate;

   const response = await platformApiService.getPayouts(params);
   setPayouts(response.data.payouts || []);
   setTotalPages(response.data.pagination?.pages || 1);
   if (response.data.stats) {
    setStats(response.data.stats);
   }
  } catch (error) {
   console.error("Error fetching payouts:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleProcessPayout = async (payoutId: string) => {
  if (!window.confirm("Are you sure you want to process this payout?")) {
   return;
  }

  try {
   await platformApiService.processPayout(payoutId);
   fetchPayouts();
  } catch (error) {
   console.error("Error processing payout:", error);
   alert("Failed to process payout");
  }
 };

 const handleCancelPayout = async (payoutId: string) => {
  if (!window.confirm("Are you sure you want to cancel this payout?")) {
   return;
  }

  try {
   await platformApiService.cancelPayout(payoutId);
   fetchPayouts();
  } catch (error) {
   console.error("Error cancelling payout:", error);
   alert("Failed to cancel payout");
  }
 };

 const getStatusColor = (status: string) => {
  switch (status) {
   case "completed":
    return "bg-green-100 text-green-800";
   case "pending":
    return "bg-yellow-100 text-yellow-800";
   case "processing":
    return "bg-blue-100 text-blue-800";
   case "failed":
    return "bg-red-100 text-red-800";
   case "cancelled":
    return "bg-gray-100 text-gray-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 if (loading && payouts.length === 0) {
  return <div className="p-8">{t("platform.payouts.loading")}</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6">
    <h1 className="text-3xl font-bold mb-4">{t("platform.payouts.title")}</h1>

    {/* Summary Cards */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
     <div className="bg-white shadow p-4">
      <div className="text-sm text-gray-500">{t("platform.payouts.totalPayouts")}</div>
      <div className="text-2xl font-bold">{stats.total}</div>
     </div>
     <div className="bg-white shadow p-4">
      <div className="text-sm text-gray-500">{t("platform.payouts.pending")}</div>
      <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
     </div>
     <div className="bg-white shadow p-4">
      <div className="text-sm text-gray-500">{t("platform.payouts.completed")}</div>
      <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
     </div>
     <div className="bg-white shadow p-4">
      <div className="text-sm text-gray-500">{t("platform.payouts.totalPaid")}</div>
      <div className="text-2xl font-bold">
       ${stats.totalAmount.toFixed(2)}
      </div>
     </div>
    </div>

    {/* Filters */}
    <div className="bg-white shadow p-4 mb-6">
     <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("platform.payouts.storeId")}
       </label>
       <input
        type="text"
        value={filters.storeId}
        onChange={(e) => setFilters({ ...filters, storeId: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
        placeholder={t("platform.payouts.filterByStoreId")}
       />
      </div>
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("platform.payouts.status")}
       </label>
       <select
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
       >
        <option value="">{t("platform.payouts.allStatuses")}</option>
        <option value="pending">{t("platform.payouts.pending")}</option>
        <option value="processing">{t("platform.payouts.processing")}</option>
        <option value="completed">{t("platform.payouts.completed")}</option>
        <option value="failed">{t("platform.payouts.failed")}</option>
        <option value="cancelled">{t("platform.payouts.cancelled")}</option>
       </select>
      </div>
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("platform.payouts.startDate")}</label>
       <input
        type="date"
        value={filters.startDate}
        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
       />
      </div>
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("platform.payouts.endDate")}
       </label>
       <input
        type="date"
        value={filters.endDate}
        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 "
       />
      </div>
     </div>
    </div>
   </div>

   <div className="bg-white shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
     <thead className="bg-gray-50">
      <tr>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Store
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Amount
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Status
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Method
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Period
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Created
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Actions
       </th>
      </tr>
     </thead>
     <tbody className="bg-white divide-y divide-gray-200">
      {payouts.map((payout) => (
       <tr key={payout._id}>
        <td className="px-6 py-4 whitespace-nowrap">
         <Link
          to={`/platform/stores/${payout.storeId._id}`}
          className="text-indigo-600 hover:text-indigo-900 font-medium"
         >
          {payout.storeId.name}
         </Link>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
         {payout.currency} {payout.amount.toFixed(2)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
         <span
          className={`px-2 py-1 text-xs font-semibold ${getStatusColor(
           payout.status
          )}`}
         >
          {payout.status}
         </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {payout.payoutMethod}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {new Date(payout.periodStart).toLocaleDateString()} -{" "}
         {new Date(payout.periodEnd).toLocaleDateString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {new Date(payout.createdAt).toLocaleDateString()}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
         <Link
          to={`/platform/payouts/${payout._id}`}
          className="text-indigo-600 hover:text-indigo-900 mr-3"
         >
          View
         </Link>
         {payout.status === "pending" && (
          <>
           <button
            onClick={() => handleProcessPayout(payout._id)}
            className="text-green-600 hover:text-green-900 mr-3"
           >
            Process
           </button>
           <button
            onClick={() => handleCancelPayout(payout._id)}
            className="text-red-600 hover:text-red-900"
           >
            Cancel
           </button>
          </>
         )}
        </td>
       </tr>
      ))}
     </tbody>
    </table>

    {payouts.length === 0 && !loading && (
     <div className="text-center py-8 text-gray-500">No payouts found</div>
    )}

    {/* Pagination */}
    {totalPages > 1 && (
     <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
      <button
       onClick={() => setPage(page - 1)}
       disabled={page === 1}
       className="px-4 py-2 border border-gray-300 disabled:opacity-50"
      >
       Previous
      </button>
      <span className="text-sm text-gray-700">
       Page {page} of {totalPages}
      </span>
      <button
       onClick={() => setPage(page + 1)}
       disabled={page === totalPages}
       className="px-4 py-2 border border-gray-300 disabled:opacity-50"
      >
       Next
      </button>
     </div>
    )}
   </div>
  </div>
 );
};

export default PlatformPayouts;

